import React, { useMemo, useCallback, useEffect, useState } from "react";
import {
  ChallengeTableChallenge,
  ChallengeTableProgramVersionItem,
} from "../../api/types/dtos/challenge/ChallengeDto";
import LoadingSpinner from "../../components/LoadingSpinner";
import { DataGrid } from "@mui/x-data-grid";
import Box from "@mui/material/Box";
import InspectionProgramVersionItemModal from "../../components/modal/InspectionProgramVersionItemModal";
import InspectionRowDetailsModal from "../../components/modal/InspectionRowDetailsModal";
import { useOrganization } from "../../contexts/OrganizationContext";

// Refactored modules
import { useChallengesController } from "./ChallengesDetailedTable/ChallengesDetailedTable.controller";
import { useChallengesGridBuilder } from "./ChallengesDetailedTable/ChallengesDetailedTable.grid";
import {
  ChallengeCellContext,
  ChallengeCellContextValue,
} from "./ChallengesDetailedTable/ChallengesDetailedTable.cellContext";

// Extracted DataGrid styles to prevent recreation on every render
const dataGridStyles = {
  maxWidth: "100%",
  minWidth: 0,
  width: "100%",
  fontFamily: "Roboto, Arial, sans-serif",
  fontSize: "1rem",
  ".MuiDataGrid-virtualScroller": {
    overflowX: "auto",
  },
  ".MuiDataGrid-cell, .MuiDataGrid-columnHeader": {
    border: "none",
  },
  ".MuiDataGrid-columnHeaders": {
    borderBottom: "2px solid",
    borderColor: "divider",
  },
  ".MuiDataGrid-cell": {
    padding: "0 12px",
    display: "flex",
    alignItems: "center",
  },
  ".MuiDataGrid-columnHeader": {
    padding: "0 12px",
    fontWeight: 700,
    backgroundColor: "#fafafa",
  },
  ".MuiDataGrid-row.row-even": {
    backgroundColor: "#e8e8e8",
    borderBottom: "1px solid rgba(224, 224, 224, 1)",
    cursor: "pointer",
    "& .MuiDataGrid-cell:first-of-type": {
      backgroundColor: "#d4d4d4",
      fontWeight: 500,
    },
    "&:hover": {
      backgroundColor: "#d1e7dd",
      "& .MuiDataGrid-cell:first-of-type": {
        backgroundColor: "#c3e6d0",
      },
    },
    "&.Mui-selected": {
      backgroundColor: "#90caf9",
      "& .MuiDataGrid-cell:first-of-type": {
        backgroundColor: "#64b5f6",
        fontWeight: 700,
      },
      "&:hover": {
        backgroundColor: "#64b5f6",
        "& .MuiDataGrid-cell:first-of-type": {
          backgroundColor: "#42a5f5",
        },
      },
    },
  },
  ".MuiDataGrid-row.row-odd": {
    backgroundColor: "#ffffff",
    borderBottom: "1px solid rgba(224, 224, 224, 1)",
    cursor: "pointer",
    "& .MuiDataGrid-cell:first-of-type": {
      backgroundColor: "#e8e8e8",
      fontWeight: 500,
    },
    "&:hover": {
      backgroundColor: "#d1e7dd",
      "& .MuiDataGrid-cell:first-of-type": {
        backgroundColor: "#c3e6d0",
      },
    },
    "&.Mui-selected": {
      backgroundColor: "#90caf9",
      "& .MuiDataGrid-cell:first-of-type": {
        backgroundColor: "#64b5f6",
        fontWeight: 700,
      },
      "&:hover": {
        backgroundColor: "#64b5f6",
        "& .MuiDataGrid-cell:first-of-type": {
          backgroundColor: "#42a5f5",
        },
      },
    },
  },
};

interface TablePaginationProps {
  page: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

interface ChallengesDetailedTableProps {
  data?: {
    challenges: ChallengeTableChallenge[];
    programVersionItems: ChallengeTableProgramVersionItem[];
  };
  loading?: boolean;
  pagination?: TablePaginationProps;
  onRefetch?: () => void;
  columnVisibilityModel?: Record<string, boolean>;
  onColumnVisibilityModelChange?: (model: Record<string, boolean>) => void;
  /** Enable row selection checkboxes (reports from anywhere). */
  enableSelection?: boolean;
  /** Controlled selected row ids (org ids). */
  selectedOrgIds?: string[];
  onSelectionChange?: (ids: string[]) => void;
}

export const getSortedPvis = (
  programVersionItems: ChallengeTableProgramVersionItem[],
) => {
  const groupedPvis: Record<
    string,
    { name: string; items: ChallengeTableProgramVersionItem[] }
  > = {};
  programVersionItems.forEach((pvi) => {
    const levelId = pvi.programLevelId || "ungrouped";
    const levelName = pvi.programLevelName || "ungrouped";
    if (!groupedPvis[levelId]) {
      groupedPvis[levelId] = { name: levelName, items: [] };
    }
    groupedPvis[levelId].items.push(pvi);
  });

  for (const levelId in groupedPvis) {
    groupedPvis[levelId].items.sort((a, b) => {
      const cpA = a.cpNumber || "";
      const cpB = b.cpNumber || "";
      return cpA.localeCompare(cpB, undefined, { numeric: true });
    });
  }

  const sortedLevels = Object.entries(groupedPvis).sort(
    ([, groupA], [, groupB]) => groupA.name.localeCompare(groupB.name),
  );

  const sortedPvis = sortedLevels.flatMap(([, group]) => group.items);
  return sortedPvis;
};

// Main Table Component using DataGrid
const ChallengesDetailedTable: React.FC<ChallengesDetailedTableProps> = ({
  data,
  loading: loadingProp,
  pagination,
  onRefetch,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  enableSelection = false,
  selectedOrgIds,
  onSelectionChange,
}): React.ReactElement | null => {
  // --- Hooks from refactored modules ---
  const { selectedOrganization } = useOrganization();

  const controller = useChallengesController({
    data: data as any,
    selectedOrganization: selectedOrganization as any,
    onRefetch,
  });

  const { columns, rows, columnGroupingModel } = useChallengesGridBuilder({
    data,
    handleOpenRowModal: controller.handleOpenRowModal,
  });

  // --- Local state ---
  const [initialLoading, setInitialLoading] = useState(true);

  // --- Effects ---
  useEffect(() => {
    if (loadingProp !== undefined) {
      setInitialLoading(loadingProp);
    }
  }, [loadingProp]);

  // --- Memoized context value ---
  const cellContextValue = useMemo<ChallengeCellContextValue>(
    () => ({
      cellState: controller.cellState,
      updating: controller.updating,
      handleCellStateChange: controller.handleCellStateChange,
      handleOpenCellModal: controller.handleOpenCellModal,
    }),
    [
      controller.cellState,
      controller.updating,
      controller.handleCellStateChange,
      controller.handleOpenCellModal,
    ],
  );

  // --- Memoize getRowId and getRowClassName ---
  const getRowId = useCallback((row: any) => row.id, []);

  const getRowClassName = useCallback(
    (params: { row: { _isEven: boolean } }) => {
      return params.row._isEven ? "row-even" : "row-odd";
    },
    [],
  );

  if (initialLoading) {
    return <LoadingSpinner />;
  }

  if (!data) {
    return <div>No data</div>;
  }

  return (
    <ChallengeCellContext.Provider value={cellContextValue}>
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          overflowX: "hidden",
        }}
      >
        <Box sx={{ height: 650, width: "100%", maxWidth: "100%", minWidth: 0 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={initialLoading}
            getRowId={getRowId}
            density="compact"
            rowHeight={56}
            columnHeaderHeight={60}
            rowBufferPx={150}
            columnBufferPx={100}
            disableVirtualization={false}
            initialState={{}}
            columnGroupingModel={columnGroupingModel}
            checkboxSelection={enableSelection}
            disableRowSelectionOnClick={!enableSelection}
            {...(enableSelection
              ? {
                  rowSelectionModel: {
                    type: 'include' as const,
                    ids: new Set(selectedOrgIds ?? []),
                  },
                  onRowSelectionModelChange: (model: any) => {
                    if (model?.type === 'include') {
                      onSelectionChange?.(
                        Array.from(model.ids as Set<string | number>).map(String)
                      );
                    } else {
                      const excluded = new Set<string>(
                        Array.from(model.ids as Set<string | number>).map(String)
                      );
                      onSelectionChange?.(
                        rows.map((row) => String(row.id)).filter((id) => !excluded.has(id))
                      );
                    }
                  },
                }
              : {})}
            {...(columnVisibilityModel !== undefined
              ? {
                  columnVisibilityModel,
                  onColumnVisibilityModelChange: onColumnVisibilityModelChange as any,
                }
              : {})}
            getRowClassName={getRowClassName}
            {...(pagination
              ? {
                  paginationMode: "server" as const,
                  paginationModel: { page: pagination.page, pageSize: pagination.pageSize },
                  rowCount: pagination.totalElements,
                  onPaginationModelChange: (model: { page: number; pageSize: number }) => {
                    if (model.pageSize !== pagination.pageSize) {
                      pagination.onPageSizeChange(model.pageSize);
                    } else {
                      pagination.onPageChange(model.page);
                    }
                  },
                  pageSizeOptions: [25, 50, 100, 200],
                }
              : {})}
            sx={dataGridStyles}
          />
        </Box>

        <InspectionProgramVersionItemModal
          open={controller.cellModalOpen}
          onClose={() => controller.setCellModalOpen(false)}
          item={controller.cellModalItem}
          formTemplateJsonProp={
            controller.cellModalTemplateJson === null
              ? undefined
              : controller.cellModalTemplateJson
          }
          onSave={(updated) => {
            if (
              !controller.cellModalCellKey ||
              !controller.cellModalItem
            )
              return;
            controller.handleCellStateChange(
              controller.cellModalCellKey,
              (controller.cellModalItem as any).id,
              updated,
            );
          }}
          programVersionItem={data.programVersionItems.find(
            (pvi) => pvi.id === controller.cellModalItem?.programVersionItemId,
          )}
          ownerName={(() => {
            if (!controller.cellModalItem) return undefined;
            const challenge = data?.challenges.find((ch) =>
              ch.inspectionsProgramsVersionsItems.some(
                (i) => i.id === (controller.cellModalItem as any).id,
              ),
            );
            return challenge?.inspectedUserName;
          })()}
        />
        <InspectionRowDetailsModal
          open={controller.rowModalOpen}
          onClose={() => controller.setRowModalOpen(false)}
          items={controller.rowModalItems}
          onSave={controller.handleSaveRowModal}
          programVersionItems={data.programVersionItems}
          ownerName={(() => {
            if (controller.rowModalItems.length === 0) return undefined;
            const firstItem = controller.rowModalItems[0];
            const challenge = data?.challenges.find((ch) =>
              ch.inspectionsProgramsVersionsItems.some(
                (i) => i.id === firstItem.id,
              ),
            );
            return challenge?.inspectedUserName;
          })()}
          farmName={(() => {
            if (controller.rowModalItems.length === 0) return undefined;
            const firstItem = controller.rowModalItems[0];
            const challenge = data?.challenges.find((ch) =>
              ch.inspectionsProgramsVersionsItems.some(
                (i) => i.id === firstItem.id,
              ),
            );
            return challenge?.inspectedOrgName;
          })()}
          phoneNumber={(() => {
            if (controller.rowModalItems.length === 0) return undefined;
            const firstItem = controller.rowModalItems[0];
            const challenge = data?.challenges.find((ch) =>
              ch.inspectionsProgramsVersionsItems.some(
                (i) => i.id === firstItem.id,
              ),
            );
            return challenge?.inspectedUserPhones?.[0];
          })()}
        />
      </Box>
    </ChallengeCellContext.Provider>
  );
};

export default ChallengesDetailedTable;
